import { create, all, MathJsStatic } from 'mathjs';
import { Temporal } from '@js-temporal/polyfill'
import { Note } from './note.model';

// This class is defined without @Injectable so that it can be compiled without
// angular code for usage on the server / Evident.
export class MathJsServiceBase {
	private math: MathJsStatic = create(all) as MathJsStatic;
	private divideAllowByZero = this.math.divide;
	private limitedEvaluate = this.math.evaluate;
	private tz: string = '';
	
	constructor() {
		// https://mathjs.org/docs/expressions/security.html
		this.math.import({
			'import': function() { throw new Error('Function import is disabled') },
			'createUnit': function() { throw new Error('Function createUnit is disabled') },
			'evaluate': function() { throw new Error('Function eval is disabled') },
			'parse': function() { throw new Error('Function parse is disabled') },
			'simplify': function() { throw new Error('Function simplify is disabled') },
			'derivative': function() { throw new Error('Function derivative is disabled') },
			'divide': (a: number, b: number) => this.newDivide(a, b),
			'divideAllowByZero': (a: number, b: number) => this.divideAllowByZero(a, b),
			'now': () => this.now(),
			'dateDiff': (a: string, b: string, unit: Temporal.PluralUnit<Temporal.DateTimeUnit>) => this.dateDiff(a, b, unit),
			'getDatePart': (a: string, part: string) => this.getDatePart(a, part),
			'setDatePart': (a: string, part: string, value: number) => this.setDatePart(a, part, value),
			'addDatePart': (a: string, part: string, value: number) => this.addDatePart(a, part, value),
			'toDate': (a: string) => this.toDate(a),
			'toTime': (a: string) => this.toTime(a),
			'stringify': (a: any) => JSON.stringify(a),
			'arrayCompare': (in_array: any[], target_array: any[], operation: string) => this.arrayCompare(in_array, target_array, operation)
		}, { override: true });
	};
	
	public evaluate(formula: string, scope: MathJsScope) {
		
		const scope2: MathJsScope = {}
		for( var k in scope ) {
			if( k === '$extValues' )
			{
				scope2[k] = {} as MathJsScope;
				for( var nK in scope[k] )
					scope2[k][nK] = scope[k][nK];
			}
			else
				scope2[k] = scope[k];
		};
		this.tz = scope['$tz'];
		var result = this.limitedEvaluate(formula, scope2);
	
		Object.keys(scope2).forEach(function(k) {
			scope[k] = scope2[k];
		});
		
		return typeof (result) == 'object' && result.valueOf
			? result.valueOf()[result.valueOf().length - 1]
			: result;
	};
	
	public round(input: number, decimals: number) {
		return this.math.round(input, decimals);
	}
	
	private newDivide(a: number, b: number) {
		if (this.math.isZero(b))
			throw new Error('Divide by zero');
		
		return this.divideAllowByZero(a, b);
	};
	
	private getTemporalType(input: string) {
		if( /[0-9]{4}-[0-9]{2}-[0-9]{2}T.*/.test(input) )
			return 'datetime';
		else if( /[0-9]{4}-[0-9]{2}-[0-9]{2}/.test(input) )
			return 'date';
		else if( /[0-9]{2}:[0-9]{2}/.test(input) )
			return 'time';

		throw new Error("Unknown temporal format: " + input);
	};
	
	private stringToZonedDateTime(input: string) {
		// 00:00 offset refuses to parse for non-UTC timezones
		if( /.*[+-]00:00$/.test(input) )
			input = input.substring(0,input.length-6) + 'Z';
		return Temporal.ZonedDateTime.from(input +'['+this.tz+']');
	}
	
	private stringToTemporal(input: string): Temporal.ZonedDateTime | Temporal.PlainDate | Temporal.PlainTime {
		switch(this.getTemporalType(input)) {
			case 'datetime':
				return this.stringToZonedDateTime(input);
			case 'date':
				return Temporal.PlainDate.from(input);
			case 'time':
				return Temporal.PlainTime.from(input);
			default:
		};
		
		throw new Error("Unknown temporal format: " + input);
	};
	
	private temporalToString(input: Temporal.ZonedDateTime | Temporal.PlainDate | Temporal.PlainTime, type: string ) {
		if( type === 'datetime' )
			return (input as Temporal.ZonedDateTime).toInstant().toString();
		else
			return input.toString();
	}
	

	private toDate(input: string) {
		return this.stringToZonedDateTime(input).toPlainDate().toString();
	}
	
	private toTime(input: string) {
		return this.stringToZonedDateTime(input).toPlainTime().toString();
	}
	
	private now() {
		return Temporal.Now.plainDateTimeISO().toString();
	}
	
	private dateDiff(a: string, b: string, unit: Temporal.PluralUnit<Temporal.DateTimeUnit>) {
		let typeA = this.getTemporalType(a);
		let typeB = this.getTemporalType(b);
		if( typeA !== typeB )
			throw new Error('Temporal type mismatch in dateDiff: ' + typeA + ' != ' + typeB + ' (' + a + ', ' + b + ')');
			
		let aT = this.stringToTemporal( a );
		let bT = this.stringToTemporal( b );
		
		return aT.until(bT.toString()).total({
			unit: unit,
			relativeTo: typeA === 'time' ? undefined : aT
		});
	}
	
	private getDatePart(input: string, part: string) {
		let type = this.getTemporalType(input),
		   inputVal = this.stringToTemporal(input);
		if( !(part in inputVal) )
			throw new Error('Unknown date part for temporal type ' + type + ": " + part);
		switch(type) {
			case 'datetime':
				let datetime = inputVal as Temporal.ZonedDateTime;
				return datetime[part as keyof Temporal.ZonedDateTime];
			case 'date':
				let date = inputVal as Temporal.PlainDate;
				return date[part as keyof Temporal.PlainDate];
			case 'time':
				let time = inputVal as Temporal.PlainTime;
				return time[part as keyof Temporal.PlainTime];
			default:
				throw new Error("Unknown type of temporal string: " + input);
		};
	}
	
	private setDatePart(input: string, part: string, value: number) {
		let inputVal = this.stringToTemporal(input),
			type = this.getTemporalType(input);
		
		switch(type) {
			case 'datetime':
				let datetime = inputVal as Temporal.ZonedDateTime,
					dtVal = {} as NumericDateTimeLike;
				dtVal[part as keyof NumericDateTimeLike] = value;
				return this.temporalToString(datetime.with(dtVal), type);
			case 'date':
				let date = inputVal as Temporal.PlainDate,
					dVal = {} as NumericDateLike;
				dVal[part as keyof NumericDateLike] = value;
				return this.temporalToString(date.with(dVal), type);
			case 'time':
				let time = inputVal as Temporal.PlainTime,
					tVal = {} as NumericTimeLike;
				tVal[part as keyof NumericTimeLike] = value;
				return this.temporalToString(time.with(tVal), type);
			default:
				throw new Error("Unknown type of temporal string: " + input);
		}
	}
	private addDatePart(input: string, part: string, value: number) {
		let inputVal = this.stringToTemporal(input),
			type = this.getTemporalType(input),
			duration = {} as Temporal.DurationLike;
		if( part.slice(-1) !== 's' )
			part = part + 's';
		duration[part as keyof Temporal.DurationLike] = value;
		
		let result = inputVal.add(duration);
		
		return this.temporalToString(result, type);
	}
	
	private arrayCompare(in_array: any[], target: any, operation: string = 'any')
	{
		var target_array: any[];
		if( target._data ) // convert mathjs matrix
			target_array = target._data;
		else
			target_array = target;
			
		if( !in_array.filter )
			in_array = [in_array];
		
		if( !target_array.filter )
			target_array = [target];
		
		var resultLength = target_array.filter(searchVal => in_array.indexOf(searchVal) >= 0).length;
		
		if( operation == 'any' )
			return resultLength > 0;
		else
			return resultLength == target_array.length;
	}
	
};

interface NumericDateLike {
	year?: number;
	month?: number;
	day?: number;
};

interface NumericTimeLike {
	hour?: number;
	minute?: number;
	second?: number;
	millisecond?: number;

}
interface NumericDateTimeLike extends NumericDateLike, NumericTimeLike {
};

export interface MathJsScope {
	[key: string]: any
};